/**
 * Brand Voice Storage Service — DISABLED
 * Feature discontinued. All methods return empty/no-op to avoid DB errors.
 * The brand_voices table no longer exists.
 */

import type { BrandVoice, BrandVoiceStorageService } from '../types/brandVoice';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

// Feature flag — set to true to re-enable brand voice (requires DB table)
const BRAND_VOICE_ENABLED = false;

// Legacy localStorage keys for migration
const LEGACY_STORAGE_KEY = 'creative_nodeflow_brand_voices';
const LEGACY_DEFAULT_BRAND_VOICE_KEY = 'creative_nodeflow_default_brand_voice';

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID
 */
function isValidUUID(id: string): boolean {
  return UUID_REGEX.test(id);
}

/**
 * Generate a new UUID (crypto.randomUUID is available in modern browsers)
 */
function generateUUID(): string {
  return crypto.randomUUID();
}

class SupabaseBrandVoiceStorageService implements BrandVoiceStorageService {
  /**
   * List all brand voices for the current user
   */
  async listBrandVoices(): Promise<BrandVoice[]> {
    if (!BRAND_VOICE_ENABLED) return [];
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.warn('[BrandVoiceStorage] No authenticated user');
        return [];
      }

      const { data, error } = await supabase
        .from('brand_voices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('[BrandVoiceStorage] Failed to list brand voices:', error);
        throw error;
      }

      // Map database format to BrandVoice type
      return (data || []).map(row => ({
        id: row.id,
        name: row.name,
        instructions: row.instructions,
        description: row.description || undefined,
        color: row.color || undefined,
        icon: row.icon || undefined,
        isDefault: row.is_default || false,
        createdAt: row.created_at,
        updatedAt: row.updated_at
      }));
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to list brand voices:', error);
      return [];
    }
  }

  /**
   * Get a specific brand voice by ID
   */
  async getBrandVoice(id: string): Promise<BrandVoice | null> {
    if (!BRAND_VOICE_ENABLED) return null;
    try {
      // Skip database query if ID is not a valid UUID (legacy ID format)
      if (!isValidUUID(id)) {
        logger.debug('[BrandVoiceStorage] Skipping query for non-UUID id:', id);
        return null;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.warn('[BrandVoiceStorage] No authenticated user');
        return null;
      }

      const { data, error } = await supabase
        .from('brand_voices')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        logger.error('[BrandVoiceStorage] Failed to get brand voice:', error);
        throw error;
      }

      if (!data) return null;

      return {
        id: data.id,
        name: data.name,
        instructions: data.instructions,
        description: data.description || undefined,
        color: data.color || undefined,
        icon: data.icon || undefined,
        isDefault: data.is_default || false,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to get brand voice:', error);
      return null;
    }
  }

  /**
   * Save or update a brand voice
   */
  async saveBrandVoice(brandVoice: BrandVoice): Promise<void> {
    if (!BRAND_VOICE_ENABLED) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Check if brand voice exists
      const existing = await this.getBrandVoice(brandVoice.id);

      const dbData = {
        id: brandVoice.id,
        user_id: user.id,
        name: brandVoice.name,
        instructions: brandVoice.instructions,
        description: brandVoice.description || null,
        color: brandVoice.color || '#3b82f6',
        icon: brandVoice.icon || null,
        is_default: brandVoice.isDefault || false
      };

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('brand_voices')
          .update(dbData)
          .eq('id', brandVoice.id)
          .eq('user_id', user.id);

        if (error) {
          logger.error('[BrandVoiceStorage] Failed to update brand voice:', error);
          throw error;
        }

        logger.debug('[BrandVoiceStorage] Brand voice updated:', brandVoice.id);
      } else {
        // Insert new
        const { error } = await supabase
          .from('brand_voices')
          .insert(dbData);

        if (error) {
          logger.error('[BrandVoiceStorage] Failed to create brand voice:', error);
          throw error;
        }

        logger.debug('[BrandVoiceStorage] Brand voice created:', brandVoice.id);
      }
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to save brand voice:', error);
      throw new Error('Failed to save brand voice');
    }
  }

  /**
   * Delete a brand voice
   */
  async deleteBrandVoice(id: string): Promise<void> {
    if (!BRAND_VOICE_ENABLED) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      const { error } = await supabase
        .from('brand_voices')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        logger.error('[BrandVoiceStorage] Failed to delete brand voice:', error);
        throw error;
      }

      logger.debug('[BrandVoiceStorage] Brand voice deleted:', id);
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to delete brand voice:', error);
      throw new Error('Failed to delete brand voice');
    }
  }

  /**
   * Get the default brand voice
   */
  async getDefaultBrandVoice(): Promise<BrandVoice | null> {
    if (!BRAND_VOICE_ENABLED) return null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.warn('[BrandVoiceStorage] No authenticated user');
        return null;
      }

      const { data, error } = await supabase
        .from('brand_voices')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found - no default set
          return null;
        }
        logger.error('[BrandVoiceStorage] Failed to get default brand voice:', error);
        throw error;
      }

      if (!data) return null;

      return {
        id: data.id,
        name: data.name,
        instructions: data.instructions,
        description: data.description || undefined,
        color: data.color || undefined,
        icon: data.icon || undefined,
        isDefault: data.is_default || false,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to get default brand voice:', error);
      return null;
    }
  }

  /**
   * Set the default brand voice
   * The database trigger will automatically unset other defaults
   */
  async setDefaultBrandVoice(id: string): Promise<void> {
    if (!BRAND_VOICE_ENABLED) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Verify the brand voice exists
      const voice = await this.getBrandVoice(id);
      if (!voice) {
        throw new Error('Brand voice not found');
      }

      // Set this voice as default (trigger will unset others)
      const { error } = await supabase
        .from('brand_voices')
        .update({ is_default: true })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        logger.error('[BrandVoiceStorage] Failed to set default brand voice:', error);
        throw error;
      }

      logger.debug('[BrandVoiceStorage] Default brand voice set:', id);
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to set default brand voice:', error);
      throw new Error('Failed to set default brand voice');
    }
  }

  /**
   * Migrate from localStorage to Supabase
   */
  async migrateFromLegacy(): Promise<void> {
    if (!BRAND_VOICE_ENABLED) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.debug('[BrandVoiceStorage] No user, skipping migration');
        return;
      }

      // Check if user already has brand voices in Supabase
      const existing = await this.listBrandVoices();
      if (existing.length > 0) {
        logger.debug('[BrandVoiceStorage] User already has brand voices, skipping migration');
        return;
      }

      // Try to load from localStorage
      const legacyData = localStorage.getItem(LEGACY_STORAGE_KEY);
      const legacyDefaultId = localStorage.getItem(LEGACY_DEFAULT_BRAND_VOICE_KEY);
      
      if (!legacyData) {
        // Check for old single brand voice
        const legacyBrandInstructions = localStorage.getItem('brandInstructions');
        if (legacyBrandInstructions) {
          // Create default brand voice from legacy data with a proper UUID
          const defaultVoice: BrandVoice = {
            id: generateUUID(),
            name: 'Default Brand Voice',
            instructions: legacyBrandInstructions,
            description: 'Migrated from legacy brand instructions',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isDefault: true
          };

          await this.saveBrandVoice(defaultVoice);
          logger.info('[BrandVoiceStorage] Migrated legacy brand instructions');
        }
        return;
      }

      // Parse and migrate localStorage brand voices
      const voices: BrandVoice[] = JSON.parse(legacyData);
      
      for (const voice of voices) {
        // Set isDefault based on legacy default ID
        const isDefault = voice.id === legacyDefaultId;
        // Generate a new UUID for each legacy brand voice
        await this.saveBrandVoice({
          ...voice,
          id: generateUUID(), // Replace legacy ID with proper UUID
          isDefault
        });
      }

      logger.info(`[BrandVoiceStorage] Successfully migrated ${voices.length} brand voices from localStorage`);
      
      // Clear localStorage after successful migration to prevent re-migration
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      localStorage.removeItem(LEGACY_DEFAULT_BRAND_VOICE_KEY);
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to migrate legacy brand voices:', error);
    }
  }

  /**
   * Clear all brand voices (for testing/reset)
   */
  async clearAll(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user');
      }

      const { error } = await supabase
        .from('brand_voices')
        .delete()
        .eq('user_id', user.id);

      if (error) {
        logger.error('[BrandVoiceStorage] Failed to clear brand voices:', error);
        throw error;
      }

      logger.debug('[BrandVoiceStorage] All brand voices cleared');
    } catch (error) {
      logger.error('[BrandVoiceStorage] Failed to clear brand voices:', error);
      throw new Error('Failed to clear brand voices');
    }
  }
}

const brandVoiceStorageService = new SupabaseBrandVoiceStorageService();
export default brandVoiceStorageService;
